// ============================================================
//  src/pages/auth/ForgotPasswordPage.js
//  3-step flow: request OTP → verify OTP → reset password
// ============================================================
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { forgotPasswordRequest, forgotPasswordVerify, forgotPasswordReset } from '../../api/client';
import './auth.css';

const getError = (err) =>
  err?.response?.data?.message || err?.message || 'Something went wrong';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  // step: 'request' | 'verify' | 'reset' | 'done'
  const [step,       setStep]       = useState('request');
  const [email,      setEmail]      = useState('');
  const [otp,        setOtp]        = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [loading,    setLoading]    = useState(false);

  // ── Step 1: Request OTP ──────────────────────────────────
  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      const { data } = await forgotPasswordRequest(email.trim().toLowerCase());
      if (data.success) {
        toast.success(data.message || 'Reset code sent! Check your inbox.');
        setStep('verify');
      } else {
        toast.error(data.message);
      }
    } catch (err) { toast.error(getError(err)); }
    finally { setLoading(false); }
  };

  // ── Step 2: Verify OTP ───────────────────────────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    if (!otp.trim()) { toast.error('Please enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const { data } = await forgotPasswordVerify(email.trim().toLowerCase(), otp.trim());
      if (data.success) {
        setResetToken(data.data?.reset_token || '');
        toast.success('Code verified! Set your new password.');
        setStep('reset');
      } else {
        toast.error(data.message);
      }
    } catch (err) { toast.error(getError(err)); }
    finally { setLoading(false); }
  };

  // ── Step 3: Reset Password ───────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Please enter a new password'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const { data } = await forgotPasswordReset(email.trim().toLowerCase(), resetToken, password);
      if (data.success) {
        toast.success('Password reset! Please log in.');
        setStep('done');
      } else {
        toast.error(data.message);
      }
    } catch (err) { toast.error(getError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-dot-grid" /><div className="auth-ring auth-r1" /><div className="auth-ring auth-r2" /><div className="auth-ring auth-r3" />
        <div className="auth-left-content">
          <span className="auth-brand"><div className="auth-brand-mark">T</div><span className="auth-brand-name">Cloverr</span></span>
        </div>
        <div className="auth-left-main">
          <div className="auth-eyebrow"><div className="auth-eyebrow-dot" /><span>Account Recovery</span></div>
          <div className="auth-headline">Reset Your<br /><em>Password</em></div>
          <div className="auth-sub">Follow the steps to securely recover your account.</div>
        </div>
        <div className="auth-left-footer"><div className="auth-ssl"><i className="fas fa-lock" /> 256-bit SSL encrypted</div></div>
      </div>

      <div className="auth-right">
        <div className="auth-shell">

          {/* ── DONE ────────────────────────────────────── */}
          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,196,140,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', fontSize: 28, color: 'var(--success)' }}>
                <i className="fas fa-check-circle" />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-dark)', marginBottom: 8 }}>Password Reset!</h2>
              <p style={{ fontSize: 14, color: 'var(--text-mid)', marginBottom: 24 }}>You can now log in with your new password.</p>
              <button className="btn-auth" onClick={() => navigate('/login')}>
                <i className="fas fa-arrow-right" /> Go to Sign In
              </button>
            </div>
          )}

          {/* ── STEP 1: Request OTP ─────────────────────── */}
          {step === 'request' && (
            <>
              <div className="auth-hdr">
                <h2>Forgot password?</h2>
                <p>Enter your email address to receive a reset code.</p>
              </div>
              <form onSubmit={handleRequest}>
                <div className="auth-field">
                  <label>Email Address</label>
                  <div className="auth-field-wrap">
                    <i className="fas fa-envelope fi" />
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>
                </div>
                <button type="submit" className="btn-auth" disabled={loading}>
                  {loading ? <><span className="spinner" /> Sending&hellip;</> : <><i className="fas fa-paper-plane" /> Send Reset Code</>}
                </button>
              </form>
              <div className="auth-bottom"><Link to="/login"><i className="fas fa-arrow-left" /> Back to Sign In</Link></div>
            </>
          )}

          {/* ── STEP 2: Verify OTP ──────────────────────── */}
          {step === 'verify' && (
            <>
              <div className="auth-hdr">
                <h2>Enter your code</h2>
                <p>We sent a 6-digit code to <strong>{email}</strong>. It expires in 15 minutes.</p>
              </div>
              <form onSubmit={handleVerify}>
                <div className="auth-field">
                  <label>6-Digit Code</label>
                  <div className="auth-field-wrap">
                    <i className="fas fa-shield-halved fi" />
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="000000"
                      maxLength={6}
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                      autoComplete="one-time-code"
                    />
                  </div>
                </div>
                <button type="submit" className="btn-auth" disabled={loading}>
                  {loading ? <><span className="spinner" /> Verifying&hellip;</> : <><i className="fas fa-check" /> Verify Code</>}
                </button>
              </form>
              <div className="auth-bottom">
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 14, fontWeight: 600 }} onClick={() => setStep('request')}>
                  <i className="fas fa-arrow-left" /> Change email
                </button>
              </div>
            </>
          )}

          {/* ── STEP 3: New Password ─────────────────────── */}
          {step === 'reset' && (
            <>
              <div className="auth-hdr">
                <h2>Set new password</h2>
                <p>Choose a strong password for your account.</p>
              </div>
              <form onSubmit={handleReset}>
                <div className="auth-field">
                  <label>New Password</label>
                  <div className="auth-field-wrap">
                    <i className="fas fa-lock fi" />
                    <input
                      type="password"
                      placeholder="Min. 6 characters"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <div className="auth-field">
                  <label>Confirm Password</label>
                  <div className="auth-field-wrap">
                    <i className="fas fa-lock fi" />
                    <input
                      type="password"
                      placeholder="Repeat password"
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <button type="submit" className="btn-auth" disabled={loading}>
                  {loading ? <><span className="spinner" /> Resetting&hellip;</> : <><i className="fas fa-key" /> Reset Password</>}
                </button>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
